/**
 * Where an update stands, in two places: a panel in Settings → About (what was
 * found, a progress bar while it downloads, the button for the next step) and a
 * line in the status bar of every layout while one is downloading or waiting to
 * install. Click the status line to open the panel.
 */

import { useEffect, useState } from "react";
import { useApp } from "../core/app";
import { downloadUpdate, installUpdate, installWhen, onUpdateStatus, updateStatus, type UpdateStatus } from "../core/updates";

export function useUpdateStatus(): UpdateStatus {
  const [s, setS] = useState(updateStatus);
  useEffect(() => onUpdateStatus(() => setS(updateStatus())), []);
  return s;
}

const mb = (bytes: number) => `${(bytes / 1048576).toFixed(1)} MB`;

function percent(s: UpdateStatus): number | null {
  return s.total ? Math.min(100, Math.round((100 * s.downloaded) / s.total)) : null;
}

/** The panel in Settings → About. Nothing while there is no update to show. */
export function UpdatePanel() {
  const s = useUpdateStatus();
  if (s.phase === "idle" || !s.version) return null;
  const pct = percent(s);
  return (
    <div className={`nv-update ${s.phase}`} role="status" aria-live="polite">
      <div className="nv-update-head">
        <b>Nova {s.version}</b>
        <span className="nv-dim">
          {s.phase === "found" && "is available."}
          {s.phase === "downloading" && (pct != null ? `downloading… ${pct}%` : "downloading…")}
          {s.phase === "ready" && (s.installOnExit ? "is downloaded and installs when you close Nova." : "is downloaded and ready to install.")}
          {s.phase === "failed" && `did not download: ${s.error ?? "unknown error"}`}
        </span>
      </div>
      {(s.phase === "downloading" || s.phase === "ready") && (
        <div className="nv-update-bar" aria-label="Download progress">
          <i style={{ width: `${s.phase === "ready" ? 100 : pct ?? 5}%` }} className={pct == null && s.phase === "downloading" ? "unknown" : ""} />
        </div>
      )}
      {s.phase === "downloading" && (
        <span className="nv-dim tnum">{mb(s.downloaded)}{s.total ? ` of ${mb(s.total)}` : ""}</span>
      )}
      <div className="nv-update-actions">
        {(s.phase === "found" || s.phase === "failed") && (
          <button className="nv-btn primary" onClick={() => void downloadUpdate()}>{s.phase === "failed" ? "Try again" : "Download"}</button>
        )}
        {s.phase === "ready" && (
          <button className="nv-btn primary" onClick={() => void installUpdate()}>Restart and install now</button>
        )}
      </div>
    </div>
  );
}

/** The status bar line, while an update downloads or waits. */
export function UpdateStatusItem() {
  const app = useApp();
  const s = useUpdateStatus();
  if (s.phase !== "downloading" && s.phase !== "ready") return null;
  const pct = percent(s);
  const text = s.phase === "downloading"
    ? `Downloading Nova ${s.version}${pct != null ? ` · ${pct}%` : "…"}`
    : s.installOnExit || installWhen() === "exit"
      ? `Nova ${s.version} installs when you close Nova`
      : `Nova ${s.version} is ready · restart to install`;
  return (
    <button className="nv-link nv-update-status" onClick={() => app.openSettings("about")} title="Open the update in Settings → About">
      {s.phase === "downloading" && <span className="nv-update-mini" aria-hidden="true"><i style={{ width: `${pct ?? 30}%` }} /></span>}
      {text}
    </button>
  );
}
